import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { CONTROLLING_PARTY_CUSTOM_ROLE_MAX_LENGTH } from '@dealstack/shared/lib/common/business/controllingParty';
import { CONTROLLING_PARTY_RELATION_TYPES } from '@dealstack/shared/lib/common/business/controllingPartyRelationTypes';
import type { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import {
	formatDateToBEIgnoreTimezone,
	parseBEDateIgnoreTimezone,
} from '@dealstack/shared/lib/format/dates/date';
import { ControllingPartyDto, PersonRelationType } from '@dealstack/shared/lib/generated';
import * as Yup from 'yup';

import { JOIN_ALLOCATION_PIV_INFO_FORM } from './useJoinAndAllocationPIVInformationController';
import {
	ControllingPartyEntry,
	ControllingPartyRelationType,
	ControllingPartyTaxResidenceEntry,
	SavedSignatory,
	SIGNATORY_CHOICE,
} from './useJoinAndAllocationPIVInformationController.types';

interface ResolvedSignatory {
	firstName: string;
	lastName: string;
	email: string;
}

const trimmed = (value?: string | null): string => value?.trim() ?? '';

export { CONTROLLING_PARTY_RELATION_TYPES };

const CONTROLLING_PARTY_RELATION_TYPE_SET = new Set<PersonRelationType>(
	CONTROLLING_PARTY_RELATION_TYPES
);

const isControllingPartyRelationType = (
	relationType: PersonRelationType
): relationType is ControllingPartyRelationType =>
	CONTROLLING_PARTY_RELATION_TYPE_SET.has(relationType);

/**
 * Yup schema for one controlling party entry — the single source of truth for
 * BOTH flows that render {@link ControllingPartyEntryFields} (investor join and
 * leave). The two controllers used to carry hand-copied variants of this object,
 * and they drifted: the leave flow missed the birth/citizenship rules and the
 * OTHER custom-role rule, letting submits through that the API then rejected
 * (MIP-2655). Keep every entry rule here, not in the controllers.
 */
export const buildControllingPartyEntrySchema = (
	commonYup: ReturnType<typeof useCommonYupValidation>,
	tCommonForm: TranslateFunction<I18Namespaces.COMMON_FORM>
) =>
	Yup.object({
		localId: commonYup.string.optional,
		email: commonYup.email.required,
		firstName: commonYup.string.required,
		lastName: commonYup.string.required,
		dateOfBirth: commonYup.date.past.optional.nullable(),
		placeOfBirth: commonYup.string.optional,
		countryOfBirth: commonYup.countryCode.optional.transform((value: string | undefined) =>
			value === '' ? undefined : value
		),
		citizenship: Yup.array().of(commonYup.countryCode.required),
		country: commonYup.country.required,
		city: commonYup.string.required,
		postCode: commonYup.string.required,
		street: commonYup.string.required,
		houseNumber: commonYup.string.required,
		taxResidences: Yup.array()
			.of(
				Yup.object({
					taxResidency: commonYup.countryCode.required,
					tin: commonYup.string.optional,
				})
			)
			.min(1, tCommonForm('field_required'))
			.test('unique-tax-countries', function uniqueTaxCountries(entries) {
				const seenCountries = new Set<string>();
				for (const [index, entry] of (entries ?? []).entries()) {
					const country = entry?.taxResidency;
					if (!country) {
						continue;
					}
					if (seenCountries.has(country)) {
						return this.createError({
							path: `${this.path}[${index}].taxResidency`,
							message: tCommonForm('duplicate_tax_residency', {
								defaultValue: 'Each tax residence must be a different country',
							}),
						});
					}
					seenCountries.add(country);
				}
				return true;
			}),
		relationshipType: commonYup.string.required,
		// Free-text role, required only for the OTHER option. The length cap
		// mirrors the DTO so an over-long role fails on the field instead of
		// coming back as an API error toast.
		customRelationType: commonYup.string.optional.when('relationshipType', {
			is: PersonRelationType.OTHER,
			then: () =>
				commonYup.string.required.max(
					CONTROLLING_PARTY_CUSTOM_ROLE_MAX_LENGTH,
					tCommonForm('common.max_length', {
						max_length: CONTROLLING_PARTY_CUSTOM_ROLE_MAX_LENGTH,
					})
				),
		}),
	});

export const createEmptyControllingPartyTaxResidence = (): ControllingPartyTaxResidenceEntry => ({
	taxResidency: '',
	tin: '',
});

/** Builds an empty controlling party entry with a fresh client-side id. */
export const createEmptyControllingParty = (): ControllingPartyEntry => ({
	localId: generateUUID(),
	email: '',
	firstName: '',
	lastName: '',
	dateOfBirth: null,
	placeOfBirth: '',
	countryOfBirth: '',
	citizenship: [],
	country: '',
	city: '',
	postCode: '',
	street: '',
	houseNumber: '',
	taxResidences: [createEmptyControllingPartyTaxResidence()],
	relationshipType: '',
	customRelationType: '',
});

/**
 * Field-name builder for a controlling party entry, e.g.
 * `controllingParties.0.email`. The template-literal return type keeps it usable
 * as a typed react-hook-form path (useWatch/setValue) while still satisfying the
 * `*FieldName: string` props on the shared inputs — AddressInput,
 * SingleTaxDetails, react-hook-form-mantine TextInput/Select.
 */
export const controllingPartyFieldName = <Key extends keyof ControllingPartyEntry>(
	index: number,
	key: Key
): `${JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES}.${number}.${Key}` =>
	`${JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES}.${index}.${key}`;

/** Field-name builder for one tax residency row of a controlling party entry. */
export const controllingPartyTaxFieldName = <Key extends keyof ControllingPartyTaxResidenceEntry>(
	index: number,
	taxIndex: number,
	key: Key
): `${JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES}.${number}.taxResidences.${number}.${Key}` =>
	`${JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES}.${index}.taxResidences.${taxIndex}.${key}`;

/**
 * Resolves the chosen authorised signatory to the name/email that gets stored
 * as the Legal Representative (REPRESENTATIVE relation). The choice is either
 * the investor themselves, one of the named controlling parties (by localId),
 * or "someone else" (inline fields).
 */
export const resolveAuthorisedSignatory = (
	signatoryChoice: string,
	controllingParties: ControllingPartyEntry[],
	other: { firstName: string; lastName: string; email: string },
	self: { firstName?: string | null; lastName?: string | null; email?: string | null },
	savedSignatory: SavedSignatory | null
): ResolvedSignatory => {
	if (signatoryChoice === SIGNATORY_CHOICE.SELF) {
		return {
			firstName: trimmed(self.firstName),
			lastName: trimmed(self.lastName),
			email: trimmed(self.email),
		};
	}

	if (signatoryChoice === SIGNATORY_CHOICE.OTHER) {
		return {
			firstName: trimmed(other.firstName),
			lastName: trimmed(other.lastName),
			email: trimmed(other.email),
		};
	}

	if (signatoryChoice === SIGNATORY_CHOICE.EXISTING_REPRESENTATIVE) {
		return {
			firstName: trimmed(savedSignatory?.firstName),
			lastName: trimmed(savedSignatory?.lastName),
			email: trimmed(savedSignatory?.email),
		};
	}

	const party = controllingParties.find((entry) => entry.localId === signatoryChoice);
	return {
		firstName: trimmed(party?.firstName),
		lastName: trimmed(party?.lastName),
		email: trimmed(party?.email),
	};
};

export const fromControllingPartyResList = (
	parties: ControllingPartyDto[]
): ControllingPartyEntry[] =>
	parties.map((party) => ({
		localId: generateUUID(),
		relationId: party.relationId,
		personId: party.personId,
		email: party.email ?? '',
		firstName: party.firstName ?? '',
		lastName: party.lastName ?? '',
		dateOfBirth: party.dateOfBirth ? parseBEDateIgnoreTimezone(party.dateOfBirth) : null,
		placeOfBirth: party.placeOfBirth ?? '',
		countryOfBirth: party.countryOfBirth ?? '',
		citizenship: party.citizenship ?? [],
		country: party.address?.country ?? '',
		city: party.address?.city ?? '',
		postCode: party.address?.postCode ?? '',
		street: party.address?.street ?? '',
		houseNumber: party.address?.houseNumber ?? '',
		taxResidences: party.taxResidences?.length
			? party.taxResidences.map((residence) => ({
					taxResidency: residence.taxResidency,
					tin: residence.tin ?? '',
				}))
			: [createEmptyControllingPartyTaxResidence()],
		relationshipType: isControllingPartyRelationType(party.relationshipType)
			? party.relationshipType
			: '',
		customRelationType: party.customRelationType ?? '',
	}));

/**
 * Infers which authorised-signatory option was previously chosen, from the
 * saved Legal Representative — matched by person id: the investor themselves,
 * one of the listed controlling parties, or "someone else".
 */
export const inferSignatoryChoice = (
	entries: ControllingPartyEntry[],
	representativePersonId: string | null | undefined,
	investorPersonId: string | null | undefined
): string => {
	if (!representativePersonId) {
		return SIGNATORY_CHOICE.SELF;
	}

	// The representative is the investor themselves → the single "Myself" option.
	if (representativePersonId === investorPersonId) {
		return SIGNATORY_CHOICE.SELF;
	}

	const matchingParty = entries.find((entry) => entry.personId === representativePersonId);

	// A distinct saved representative becomes its own pre-selected option.
	return matchingParty ? matchingParty.localId : SIGNATORY_CHOICE.EXISTING_REPRESENTATIVE;
};

export const toControllingPartyDtos = (entries: ControllingPartyEntry[]): ControllingPartyDto[] =>
	entries
		.filter(
			(
				entry
			): entry is ControllingPartyEntry & {
				relationshipType: ControllingPartyRelationType;
			} => entry.relationshipType !== ''
		)
		.map((entry) => ({
			relationId: entry.relationId,
			email: trimmed(entry.email),
			firstName: trimmed(entry.firstName),
			lastName: trimmed(entry.lastName),
			dateOfBirth: entry.dateOfBirth
				? formatDateToBEIgnoreTimezone(entry.dateOfBirth)
				: undefined,
			placeOfBirth: trimmed(entry.placeOfBirth) || undefined,
			countryOfBirth: entry.countryOfBirth || undefined,
			citizenship: entry.citizenship.length ? entry.citizenship : undefined,
			address: {
				country: entry.country,
				city: entry.city,
				postCode: entry.postCode,
				street: entry.street,
				houseNumber: entry.houseNumber,
			},
			taxResidences: entry.taxResidences
				.filter((residence) => residence.taxResidency)
				.map((residence) => ({
					taxResidency: residence.taxResidency,
					tin: trimmed(residence.tin) || undefined,
				})),
			relationshipType: entry.relationshipType,
			customRelationType:
				entry.relationshipType === PersonRelationType.OTHER
					? trimmed(entry.customRelationType) || undefined
					: undefined,
		}));

/** Identity key for a controlling party entry: person (email) + relationship type. */
export const controllingPartyTypeKey = (entry: ControllingPartyEntry): string =>
	`${entry.email.trim().toLowerCase()}|${entry.relationshipType}`;

/**
 * True when two entries assign the same role to the same person — the backend
 * models that as a single relationship, so the UI must not allow creating it
 * twice.
 */
export const hasDuplicateControllingPartyType = (entries: ControllingPartyEntry[]): boolean => {
	const seen = new Set<string>();
	for (const entry of entries) {
		if (!entry.email.trim() || !entry.relationshipType) {
			continue;
		}
		const key = controllingPartyTypeKey(entry);
		if (seen.has(key)) {
			return true;
		}
		seen.add(key);
	}
	return false;
};

export const getDuplicateControllingPartyIndices = (
	entries: ControllingPartyEntry[]
): Set<number> => {
	const seen = new Set<string>();
	const duplicates = new Set<number>();
	entries.forEach((entry, index) => {
		if (!entry.email.trim() || !entry.relationshipType) {
			return;
		}
		const key = controllingPartyTypeKey(entry);
		if (seen.has(key)) {
			duplicates.add(index);
		} else {
			seen.add(key);
		}
	});
	return duplicates;
};
